import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Links from "../../../src/Data/Links.js";
import { MONTHS } from "../../constants";
import CarLoading from "../../images/487.gif";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const SalesLeaderBoard = () => {
  const thisMonth = Object.values(MONTHS)[new Date().getMonth()];
  const thisYear = new Date().getFullYear();
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");

  const columnHelper = createColumnHelper();

  const columns = useMemo(
    () => [
      columnHelper.accessor("Rank", {
        header: "Rank",
        cell: (info) => info.getValue(),
        size: 80,
        meta: { align: "center" },
      }),
      columnHelper.accessor("Name", {
        header: "Name",
        cell: (info) => info.getValue(),
        meta: { align: "left" },
      }),
      columnHelper.accessor("Division", {
        header: "Division",
        cell: (info) => info.getValue(),
        meta: { align: "left" },
      }),
      columnHelper.accessor("Store", {
        header: "Store",
        cell: (info) => info.getValue(),
        meta: { align: "left" },
      }),
      columnHelper.accessor("Total Gross Sales", {
        header: "Total Gross Sales",
        cell: (info) => info.getValue(),
        size: 150,
        meta: { align: "left" },
        sortingFn: (rowA, rowB, columnId) => {
          // Extract numeric value from formatted string (remove $ and commas)
          const valueA = parseFloat(rowA.getValue(columnId).replace(/[$,]/g, '')) || 0;
          const valueB = parseFloat(rowB.getValue(columnId).replace(/[$,]/g, '')) || 0;
          return valueA - valueB;
        },
      }),
      columnHelper.accessor("Sold Units - New", {
        header: "Sold Units - New",
        cell: (info) => info.getValue(),
        size: 140,
        meta: { align: "center" },
      }),
      columnHelper.accessor("Sold Units - Used", {
        header: "Sold Units - Used",
        cell: (info) => info.getValue(),
        size: 140,
        meta: { align: "center" },
      }),
      columnHelper.accessor("Total Units Sold", {
        header: "Total Units Sold",
        cell: (info) => info.getValue(),
        size: 150,
        meta: { align: "center" },
      }),
    ],
    [columnHelper]
  );

  const table = useReactTable({
    data: salesData,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    const getMonthlyData = (year, month) => {
      const requestBody = {
        year: year,
        month: month,
        division: "auto",
      };
      axios
        .get(Links.getAllSalespersonMetrics, requestBody)
        .then((result) => {
          setLoading(false);

          if (result.data && result.data.success && result.data.count > 0) {
            // Sort by total units sold descending
            let sortedByTotalUnitsSold = [...result.data.data].sort(
              (a, b) =>
                Number(b.new_units) +
                Number(b.used_units) -
                (Number(a.new_units) + Number(a.used_units))
            );

            const date = new Date(),
              currentYear = date.getFullYear(),
              currentMonth = monthNames[date.getMonth()];
            // filter the sorted data for the current month and year
            const sortedAndFiltered = sortedByTotalUnitsSold.filter(
              (item) => item.year === currentYear && item.month === currentMonth
            );

            let salesData = sortedAndFiltered.map((item, idx) => {
              return {
                Rank: idx + 1,
                Name: item.name
                  ? item.name.replace(/\b\w/g, (l) => l.toUpperCase())
                  : "",
                Division: "Auto",
                Store: item.storeName,
                "Sold Units - New": item.new_units,
                "Sold Units - Used": item.used_units,
                "Total Units Sold": (
                  Number(item.new_units) + Number(item.used_units)
                ).toLocaleString(),
                "Total Gross Sales": `$${(
                  Math.round(item.total_gross * 100) / 100
                ).toLocaleString()}`,
              };
            });

            setSalesData(salesData);
          } else {
            console.log("No data found for the given month and year.");
          }
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    };
    getMonthlyData(thisYear, thisMonth);
  }, []);

  if (loading) {
    return (
      <div
        style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
          width: '100%'
        }}
        className="text-center"
      >
        <img src={CarLoading} alt="Loading..." />
      </div>
    );
  } else {
    if (!salesData || salesData.length === 0) {
      return <p>No data found for the current month and year.</p>;
    }

    return (
      <div style={{ margin: 20 }}>
        <div style={{ marginBottom: 10 }}>
          <input
            type="text"
            placeholder="Search all columns..."
            value={globalFilter ?? ""}
            onChange={(e) => setGlobalFilter(e.target.value)}
            style={{
              padding: "8px 12px",
              fontSize: "14px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "300px",
            }}
          />
        </div>

        <div style={{ overflowX: "auto" }}>
          <table
            className="table table-bordered table-striped"
            style={{ width: "100%", background: "white", minWidth: "800px" }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        background: "#f5f5f5",
                        cursor: header.column.getCanSort()
                          ? "pointer"
                          : "default",
                        userSelect: "none",
                        padding: "12px",
                        textAlign:
                          header.column.columnDef.meta?.align || "left",
                        width: header.getSize(),
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          justifyContent:
                            header.column.columnDef.meta?.align === "center"
                              ? "center"
                              : header.column.columnDef.meta?.align === "right"
                              ? "flex-end"
                              : "flex-start",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " 🔼",
                          desc: " 🔽",
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #ddd",
                        fontWeight: "normal",
                        textAlign: cell.column.columnDef.meta?.align || "left",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: "10px", fontSize: "14px", color: "#666" }}>
          Showing {table.getRowModel().rows.length} of{" "}
          {table.getPreFilteredRowModel().rows.length} results
        </div>
      </div>
    );
  }
};

export default SalesLeaderBoard;
